import { ColorTypes, actions } from "@omegagrid/core";
import { ColumnSource, RowSource, CellSource, CellModel, StyleSource, Border } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import { NodeSource } from "@omegagrid/tree";
import { CommandResult } from "@omegagrid/commands";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, CellState, DEFAULT_GRID_EDITOR_OPTIONS } from "@reporting/rp-app-host";
import { DimensionDefinition, VirtualTreeRow, StatementDefinition } from "@reporting/rp-types/definition";
import { MODULE_CODES } from "@reporting/rp-types/accounting";
import { TreeGridRow, TreeGridCell } from "../../types";
import { FinDropdownAdapter, DimTreeAdapter } from "../../adapters";

export class FinTreeGridEditorAdapter extends GridEditorAdapter {
	private _columns: ColumnSource[];
	private dims: DimensionDefinition[];
	private loadedYear: number = null;
	private hasAccounts: boolean;
	private a: number;
	private editable: boolean = true;

	constructor (
		private statement: StatementDefinition,
		private year: number = null,
		private idRow: number = null,
	) {
		super();
		this.a = statement.accounting;
		this.editable = this.a === rp.accountingProvider.a;
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns && this.year == this.loadedYear) return this._columns;
		this.loadedYear = this.year;
		this.dims = await rp.ms('definition').get<DimensionDefinition[]>(`/visibleDimensions/${MODULE_CODES.FINANCE}`, { accounting: this.a });
		this.hasAccounts = Boolean(this.year);
		const rowsDropdownAdapter =  Object.assign(new FinDropdownAdapter('rows', this.statement.id_statement.toString()), { accounting: this.a });
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 4,
			filter: false,
			sorting: false,
			hidden: !this.editable,
		}, {
			data: '_id_row',
			collapsed: true,
			header: '#',
			e: false,
			t: 't',
			w: 80,
			filter: false
		}, {
			data: 'id_row_parent',
			header: msg('Parent'),
			t: 's',
			w: 300,
			l: 1,
			adapter: rowsDropdownAdapter,
			props: { D_STATEMENT_ROW_ACCT: 'ID_ROW_PARENT', D_STATEMENT_STRUCTURE: 'ID_ROW_PARENT' },
		}, {
			data: 'id_row',
			header: msg('Row'),
			t: 's',
			w: 300,
			levelIndent: true,
			levelButtons: true,
			adapter: rowsDropdownAdapter,
			props: { D_STATEMENT_STRUCTURE: 'ID_ROW' }
		}, {
			data: 'id_account',
			header: msg('Account'),
			t: 's',
			validation: { allowInvalid: true },
			w: 300,
			hidden: !this.hasAccounts,
			adapter:  Object.assign(new FinDropdownAdapter('accounts'), { accounting: this.a }),
			props: { D_STATEMENT_ROW_ACCT: 'ID_ACCOUNT' }
		}, {
			data: 'negative_flag',
			header: '⛔',
			t: 'b',
			w: 35,
			hidden: !this.hasAccounts,
			props: { D_STATEMENT_ROW_ACCT: 'NEGATIVE_FLAG' }
		}, {
			data: 'coef',
			header: msg('Coefficient'),
			t: 'n',
			w: 80,
			props: { D_STATEMENT_STRUCTURE: 'COEF', D_STATEMENT_ROW_ACCT: 'COEF' }
		}];
		if (this.hasAccounts) {
			this._columns.push({
				data: 'side',
				header: msg('Side'),
				t: 's',
				w: 80,
				e: false,
				content: [
					{ k: null, v: msg('Turnover') },
					{ k: 'D', v: msg('Debit') },
					{ k: 'C', v: msg('Credit') }
				],
				props: { D_STATEMENT_ROW_ACCT: 'SIDE' }
			});
			this.dims.forEach(dim => {
				this._columns.push({
					data: `id_dim_${dim.dim_num}`,
					header: dim.description,
					t: 's',
					w: 160,
					validation: { allowInvalid: true },
					collapsed: true,
					adapter: Object.assign(new DimTreeAdapter(MODULE_CODES.FINANCE, dim.dim_num, { selectMode: 'single', autoClose: true}), { accounting: this.a }),
					props: { D_STATEMENT_ROW_ACCT: `ID_DIM_${dim.dim_num}` }
				});
				if (dim.properties) {
					this._columns.push({
						data: `property_${dim.dim_num}`,
						header: 'Attributes',
						t: 't',
						w: 160,
						l: 1,
						props: { D_STATEMENT_ROW_ACCT: `PROPERTY_${dim.dim_num}` }
					});
				}
			});
		}
		this._columns.push({
			data: 'ord_num',
			header: msg('Order'),
			t: 'n',
			z: '0',
			w: 80,
			hidden: this.hasAccounts,
			props: { D_STATEMENT_STRUCTURE: 'ORD_NUM' }
		}, {
			data: 'result_sign',
			header: `${msg('Value')} ${msg('Cb')}`,
			t: 's',
			w: 90,
			hidden: this.hasAccounts,
			props: { D_STATEMENT_STRUCTURE: 'RESULT_SIGN' },
			content: [
				{ k: 0, v: '+ / -' },
				{ k: 1, v: '+'},
				{ k: -1, v: '-' }
			]
		});
		if (!this.hasAccounts) {
			this.dims.forEach(dim => {
				this._columns.push({
					data: `cumul_lev_dim_${dim.dim_num}`,
					header: `Level - ${dim.description}`,
					t: 's',
					w: 120,
					validation: { allowInvalid: true },
					props: { D_STATEMENT_ROW: `CUMUL_LEV_DIM_${dim.dim_num}` },
					content: [
						{ k: '', v: '' },
						{ k: 1, v: '1'},
						{ k: 2, v: '2'},
						{ k: 3, v: '3'},
						{ k: 4, v: '4'},
						{ k: 5, v: '5'},
						{ k: 6, v: '6'},
						{ k: 7, v: '7'},
						{ k: 8, v: '8'},
						{ k: 9, v: '9'}
					]
				});
			});
		}
		this._columns.push({
			data: 'idz',
			t: 'n',
			props: { D_STATEMENT_STRUCTURE: 'IDZ', D_STATEMENT_ROW_ACCT: 'IDZ' },
			e: false,
			hidden: true
		}, {
			data: 'record_type',
			t: 't',
			props: { record_type: true },
			e: false,
			hidden: true
		});
		return this._columns;
	}

	private createCell(tr: VirtualTreeRow, col: ColumnSource): Partial<CellSource> | null {
		if (col.hidden) return null;
		const isRow = tr.record_type === 'R';
		const isAccount = tr.record_type === 'A';
		switch (col.data as string) {
			case 'controls':
				return { content: this.getRowControls(tr.record_type) };
			case '_id_row':
				return { v: tr.id_row }
			case 'id_row_parent':
				return { v: tr.id_row_parent, dv: tr.row_parent, e: isRow };
			case 'id_row':
				return {
					v: tr.id_row,
					dv: [
						tr.is_aggregated ? '(A) ' : null,
						tr.is_computed ? '(C) ' : null,
						tr.row
					].filter(Boolean).join(''),
					e: this.editable && isRow,
					t: isRow ? 's' : 't'
				};
			case 'id_account':
				return {
					v: tr.id_account,
					dv: tr.account,
					e: this.editable && isAccount,
					t: isAccount ? 's' : 't',
					s: this.getInvalidCellStyle(!!tr.account_valid)
				};
			case 'negative_flag':
				if (!isAccount) return { e: false, t: 't', v: 'null', dv: '' };
				return null;
			case 'side':
				return { v: tr.side , dv: !tr.side && isAccount ? msg('Turnover') : undefined, e: this.editable && isAccount, t: this.editable && isAccount ? 's' : 't' };
		}
		if (this.hasAccounts) {
			if ((col.data as string)?.startsWith('id_dim_')) {
				const dimNum = (col.data as string).split('_')[2];
				return {
					v: tr[`id_dim_${dimNum}` as keyof VirtualTreeRow] as string,
					dv: tr[`dim_${dimNum}` as keyof VirtualTreeRow] as string,
					e: this.editable && isAccount,
					t: this.editable && isAccount ? 's' : 't',
					s: this.getInvalidCellStyle(!!tr[`dim_${dimNum}_valid` as keyof VirtualTreeRow])
				}
			}
			if ((col.data as string)?.startsWith('property_')) {
				const propNum = (col.data as string).split('_')[1];
				return {
					v: tr[`property_${propNum}` as keyof VirtualTreeRow] as string,
					e: isAccount,
				}
			}
		}
		return null;
	}

	getInvalidCellStyle(valid: boolean): StyleSource {
		if (valid) return null;
		const border: Border = { style: 'medium', color: "ab0000ff" };
		return {
			top: border,
			bottom: border,
			left: border,
			right: border
		}
	}

	async getRows(): Promise<RowSource[]> {
		const tree = await rp.ms('definition').get<VirtualTreeRow[]>(
			`statements/${this.statement.id_statement}/tree`,
			{ year: this.year, idRow: this.idRow, accounting: this.a }
		);
		//console.log('TREE ROWS', tree);
		const rows: RowSource[] = this.idRow > 0 ? [] : [{ data: {
			controls: { content: this.getRowControls('root') },
			_id_row: { v: 0, dv: '' },
			id_row: { v: 0, dv: '<root>' },
			negative_flag: { t: 't', v: 'null', dv: '' }
		}, e: false, l: 0, props: { isRoot: true } }];
		for (const tr of tree) {
			const rs: RowSource = { data: tr, l: tr.level };
			for (const col of this._columns) {
				const cell = this.createCell(tr, col);
				if (cell) rs.data[col.data] = cell;
			}
			rows.push(rs);
		}
		return rows;
	}

	getRowControls = (record_type: string) => {
		const controls = [];
		switch (record_type) {
			case 'R':
				controls.push({
					action: 'delete',
					title: msg('Delete'),
					icon: 'trash',
					color: ColorTypes.Red,
				}, {
					action: 'subtree',
					title: msg('Subtree'),
					icon: 'list-tree',
					color: ColorTypes.Gray
				}, {
					action: 'newRow',
					title: `+${msg('Row')}`,
					icon: 'plus',
					color: ColorTypes.Green
				});
				if (this.hasAccounts) {
					controls.push({
						action: 'newAccount',
						title: `+${msg('Account')}`,
						icon: 'plus',
						color: ColorTypes.Blue
					});
				}
				break;
			case 'A':
				controls.push({
					action: 'delete',
					title: msg('Delete'),
					icon: 'trash',
					color: ColorTypes.Red,
				});
				break;
			case 'root':
				controls.push({
					action: 'newRow',
					title: `+${msg('Row')}`,
					icon: 'plus',
					color: ColorTypes.Green
				});
				break;
			default:
				controls.push({
					action: 'delete',
					title: msg('Delete'),
					icon: 'trash',
					color: ColorTypes.Red,
				});
		}
		return controls;
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		const years = await rp.ms('analytics').get<string[]>('years/all', { accounting: this.a });
		const yearsList: NodeSource[] = [{ k: null, v: '', icon: {icon: 'circle-xmark', color: 'red'} }];
		yearsList.push(...years.map(year => ({ k: year, v: year })));
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			refreshAfterSave: true,
			rowTree: false,
			columnHeaderTreeButtons: true,
			levelButtonCell: { r: -2, c: 3 },
			editable: this.editable,
			pageSize: null,
			freezeLeft: 4,
			toolbar: {
				save: { enabled: this.editable },
				discard: { enabled: this.editable },
				new: {enabled: false},
				export: {
					text: 'Export',
					color: ColorTypes.Gray,
					icon: 'download',
					items: [
						{key: 'export.xlsx', value: 'XLSX', icon: 'file-excel'},
					],
					enabled: true
				}
			},
			exportTypes: ['xlsx'],
			customToolbar: [{
				type: 'dropdown',
				id: 'ddYear',
				caption: msg('Year'),
				width: 100,
				value: this.year,
				items: yearsList
			}, ...(this.editable ? [{
				type: 'button',
				id: 'copyTree',
				text: msg('Copy tree'),
				icon: 'clone',
				color: ColorTypes.Gray,
				disabled: !this.editable
			}] : [])] as ToolbarItem[],
			rowMenu: {
				deleteRows: this.editable,
			},
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				// colorize account cell if negative_flag is true
				if (cell.column?.data == 'id_account' && cell.row?.data?.negative_flag) {
					div.style.color = "#ab0000ff";
				}
				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			}
		});
	}

	getGridRows(rowData: RowData[], mustContainCProps: boolean = true): TreeGridRow[] {
		const gridRows = this.editor.grid.model.rows.filter(row =>
			rowData.some(dataRow => dataRow.index === row.index) && !row.properties?.isRoot
		);
		return gridRows.map(row => {
			const state: CellState = row.getProperty('deleted')
				? 'deleted'
				: (row.getProperty('new') ? 'new' : 'edited');

			const cells = row.model.getCellArray(row.range)
				.filter(cell => !mustContainCProps || cell.column.properties)
				.map(cell => ({
					data: cell.column.data,
					c: cell.column.index,
					cProps: cell.column.properties,
					v: cell.value,
					edited: cell.properties?.edited || false
				})) as TreeGridCell[];
			return { cells, state, index: row.index, props: row.properties, data: row.data } as TreeGridRow;
		});
	}

	async save(rowData: RowData[]) {
		const rows = this.getGridRows(rowData);
		const data = rows.map(row => Object.assign({ year: this.year, id_statement: this.statement.id_statement }, row));
		const saved = await rp.ms('definition').post('statements/tree', data);
		// On success the endpoint returns the saved records; a null result means the request
		// failed (the client reports the error without throwing). Surface it so GridEditor.save()
		// keeps the pending edits instead of clearing them as if the save had succeeded.
		if (saved == null) throw new Error('Statement tree save failed');
	}

	async onAction(action: string, cell: CellModel, event: actions.ActionEvent) {
		switch (action) {
			// toolbar actions
			case 'ddYear':
				let command: CommandResult = null;
				if (!this.editor.isSaved) {
					command = await rp.host.openMenu({
						content: 'yes_no',
						text: msg('Discard changes and change year?')
					});
				}
				if (!command || command?.id == 'Y') {
					this.year = event.action.value ? parseInt(event.action.value as string) : null;
					this.editor.clearUpdates(true);
					this.editor.refresh();
				}
				break;
			case 'copyTree':
				rp.host.openModalSidebar({
					id: `definition-finTreeCopy-${this.statement.id_statement}-${this.year}`,
					caption: `${msg('Copy tree')} - ${this.statement.statement}`
				});
				break;
			// grid actions
			case 'newAccount':
			case 'newRow':
				const isAcct = action == 'newAccount';
				const row = this.editor.grid.editing.insertRows({index: cell.row.index, count: 1, after: true})[0];
				if (row.parent) row.parent.deleteChildren([row]);
				cell.row.insertChildren([row], 0);
				row.setProperty('new', true);
				row.cell(0).value = this.getRowControls(null);
				row.cell(2).value = row.parent?.data.id_row || null;
				row.cell(2).displayValue = row.parent?.data.row || null;
				row.cell(2).setProperty('edited', true);
				row.data = { coef: 1, record_type: isAcct ? 'A' : 'R' };
				row.model.columns.forEach(column => {
					if (column.index == 0 || !column.properties) return;
					const cell: CellModel = row.cell(column.index);
					if (column.properties && column.properties['D_STATEMENT_STRUCTURE'] && column.properties['D_STATEMENT_ROW_ACCT']) {
						cell.editable = true;
					} else if (column.properties['D_STATEMENT_STRUCTURE']) {
						cell.editable = !isAcct;
						if (['t', 's'].indexOf(cell.type) > -1) {
							cell.type = !isAcct ? 's' : 't';
						} else if (cell.type == 'b' && isAcct) {
							cell.type = 't';
							cell.value = null;
							cell.displayValue = '';
						}
					} else if (column.properties['D_STATEMENT_ROW_ACCT']) {
						cell.editable = isAcct;
						if (['t', 's'].indexOf(cell.type) > -1) {
							cell.type = isAcct ? 's' : 't';
						} else if (cell.type == 'b' && !isAcct) {
							cell.type = 't';
							cell.value = null;
							cell.displayValue = '';
						}
					}
				});
				this.editor.newRows.add(row);
				this.editor.dispatchEvent(new CustomEvent('edit', {detail: action, bubbles: true, composed: true}));
				break;
			case 'subtree':
				rp.host.open(
					`definition-finTree-${this.statement.id_statement}-${this.year || 0}-${cell.row.data.id_row}`,
					{ title: cell.row.data.row }
				);
				break;
		}
	}

}
